import { cn } from '@/lib/utils';

type Geo = GeoJSON.Polygon | GeoJSON.MultiPolygon;

// Anéis externos (lon,lat) de cada polígono do talhão.
function ringsOf(g: Geo): number[][][] {
  if (g.type === 'Polygon') return g.coordinates.length ? [g.coordinates[0]!] : [];
  return g.coordinates.map((p) => p[0]!).filter(Boolean);
}

// Preview estático dos talhões importados do KML — sem tiles, só os contornos
// (igual ao Figma: fundo verde escuro, polígonos brancos, número no centro).
export function MiniMapaTalhoes({
  talhoes,
  className,
}: {
  talhoes: { numero: string; geojson: Geo }[];
  className?: string;
}) {
  const polys = talhoes.map((t) => ({ numero: t.numero, rings: ringsOf(t.geojson) }));
  const all = polys.flatMap((p) => p.rings.flat());
  if (all.length < 3) return null;

  const xs = all.map((c) => c[0]!);
  const ys = all.map((c) => c[1]!);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const W = 600, H = 300, pad = 20;
  const dx = maxX - minX || 1e-6;
  const dy = maxY - minY || 1e-6;
  const scale = Math.min((W - 2 * pad) / dx, (H - 2 * pad) / dy);
  const ox = (W - scale * dx) / 2;
  const oy = (H - scale * dy) / 2;
  const px = (lon: number) => ox + (lon - minX) * scale;
  const py = (lat: number) => H - (oy + (lat - minY) * scale); // flip Y

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      className={cn('block h-full w-full', className)}
      role="img"
      aria-label="Prévia dos talhões"
    >
      <rect x="0" y="0" width={W} height={H} fill="#37431f" />
      {polys.map((p, i) => {
        const centroids: number[][] = [];
        const paths = p.rings.map((ring) => {
          let cx = 0, cy = 0;
          const d = ring
            .map((c, j) => {
              const x = px(c[0]!), y = py(c[1]!);
              cx += x; cy += y;
              return `${j === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(' ');
          centroids.push([cx / ring.length, cy / ring.length]);
          return `${d}Z`;
        });
        const c = centroids[0] ?? [W / 2, H / 2];
        return (
          <g key={i}>
            {paths.map((d, k) => (
              <path key={k} d={d} fill="rgba(255,255,255,0.10)" stroke="#ffffff" strokeWidth={1.6} strokeLinejoin="round" />
            ))}
            <text
              x={c[0]}
              y={c[1]}
              fill="#ffffff"
              fontSize="14"
              fontWeight="600"
              textAnchor="middle"
              dominantBaseline="central"
              style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.35)', strokeWidth: 3 }}
            >
              {p.numero}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
